import type { TFunction } from "i18next";

import { EventOrigin, Events, track } from "apis/procore";
import type { AppDispatch } from "app/store";
import type { DispatchHidden } from "contexts/hidden/reducer";
import type { DispatchHighlighted } from "contexts/highlighted/reducer";
import type { DispatchSelectionBasket } from "contexts/selectionBasket/reducer";
import { ObjectVisibility } from "features/render/types";
import {
    hideObjects,
    isolateObjects,
    isolateObjectsInXRayMode,
    showObjects,
    unisolateObjects,
    unisolateObjectsFromXRayMode,
} from "utils/objectActions";
import type { IdList, SelectionSet } from "utils/selectionSet";

export interface BulkAction {
    id: string;
    label: string;
    onClick: () => void;
    disabled: boolean;
    dividerAbove?: boolean;
}

interface CreateBulkActionsDependencies {
    t: TFunction;
    dispatch: AppDispatch;
    dispatchHighlighted: DispatchHighlighted;
    dispatchHidden: DispatchHidden;
    dispatchSelectionBasket: DispatchSelectionBasket;
    defaultVisibility: ObjectVisibility;
    /** The visibility that was active before the user isolated. Set by isolateObjects/isolateObjectsInXRayMode.
     * When undefined, the current defaultVisibility was not set by the Isolate action. */
    preIsolationVisibility: ObjectVisibility | undefined;
    flyToSelected: () => void;
    currentMainObject?: number;
    onActionComplete?: () => void;
    hiddenIds: SelectionSet;
    lastHiddenIds: Iterable<number>;
    isolationVisibility?: {
        visibleIds: SelectionSet | undefined;
        keepIds: SelectionSet | undefined;
        setIdsVisible: (ids: IdList, visible: boolean) => void;
    };
    /** The origin to report in analytics events. Defaults to ObjectTree. */
    origin?: EventOrigin;
}

export function createBulkActions(
    selectedCount: number,
    highlightedIds: IdList,
    dependencies: CreateBulkActionsDependencies,
): BulkAction[] {
    const {
        t,
        dispatch,
        dispatchHighlighted,
        dispatchHidden,
        dispatchSelectionBasket,
        defaultVisibility,
        preIsolationVisibility,
        flyToSelected,
        currentMainObject,
        onActionComplete,
        hiddenIds,
        lastHiddenIds,
        isolationVisibility,
        origin = EventOrigin.ObjectTree,
    } = dependencies;
    const isolationVisibleIds = isolationVisibility?.visibleIds;

    const areAllHidden =
        highlightedIds.length > 0 &&
        highlightedIds.every((id: number) => (isolationVisibleIds ? !isolationVisibleIds.has(id) : hiddenIds.has(id)));
    // Only show "Unisolate" when we have a stored restore visibility.
    // preIsolationVisibility being set is the signal that the current Transparent/Xray mode has an associated
    // prior visibility to restore (e.g., from isolateObjects/isolateObjectsInXRayMode or bookmark/viewpoint restore),
    // not another feature (BimTrack, BimCollab, Deviations, etc.) changing it without storing a restore state.
    const areAllIsolated =
        highlightedIds.length > 0 &&
        !areAllHidden &&
        defaultVisibility === ObjectVisibility.Transparent &&
        preIsolationVisibility !== undefined;
    const areAllIsolatedFromXRayMode =
        highlightedIds.length > 0 &&
        !areAllHidden &&
        defaultVisibility === ObjectVisibility.Xray &&
        preIsolationVisibility !== undefined;

    return [
        {
            id: "hide-selected",
            label: areAllHidden ? t("unhideSelected") : t("hideSelected"),
            onClick: () => {
                if (isolationVisibleIds) {
                    isolationVisibility?.setIdsVisible(highlightedIds, areAllHidden);
                } else if (areAllHidden) {
                    showObjects(highlightedIds, {
                        dispatch,
                        dispatchHidden,
                    });
                } else {
                    hideObjects(highlightedIds, {
                        dispatch,
                        dispatchHighlighted,
                        dispatchHidden,
                        dispatchSelectionBasket,
                        currentMainObject,
                        currentHiddenIds: hiddenIds,
                        currentLastHiddenIds: lastHiddenIds,
                        preserveSelection: true,
                        includeMainObjectToHide: true,
                    });
                }
                void track(Events.bimObjects.hidden, { origin });
                onActionComplete?.();
            },
            disabled: selectedCount === 0,
        },
        {
            id: "isolate",
            label: areAllIsolated ? t("unisolate") : t("isolate"),
            onClick: () => {
                if (areAllIsolated) {
                    unisolateObjects({
                        dispatch,
                        isolationIds: isolationVisibility?.keepIds?.sortedIds ?? [],
                        restoreToVisibility: preIsolationVisibility ?? ObjectVisibility.Neutral,
                    });
                    void track(Events.bimObjects.unisolated, { origin });
                } else {
                    isolateObjects({
                        dispatch,
                        isolationIds: highlightedIds,
                        currentDefaultVisibility: preIsolationVisibility ?? defaultVisibility,
                    });
                    void track(Events.bimObjects.isolated, { origin });
                }
                onActionComplete?.();
            },
            disabled: selectedCount === 0,
        },
        {
            id: "isolate-xray",
            label: areAllIsolatedFromXRayMode ? t("unisolateFromXRayMode") : t("isolateInXRayMode"),
            onClick: () => {
                if (areAllIsolatedFromXRayMode) {
                    unisolateObjectsFromXRayMode({
                        dispatch,
                        restoreToVisibility: preIsolationVisibility ?? ObjectVisibility.Neutral,
                    });
                    void track(Events.bimObjects.unisolatedFromXray, { origin });
                } else {
                    isolateObjectsInXRayMode({
                        dispatch,
                        currentDefaultVisibility: preIsolationVisibility ?? defaultVisibility,
                    });
                    void track(Events.bimObjects.isolatedInXray, { origin });
                }
                onActionComplete?.();
            },
            disabled: selectedCount === 0,
        },
        {
            id: "zoom-to-selection",
            label: t("zoomToSelection"),
            onClick: () => {
                flyToSelected();
                void track(Events.bimObjects.zoomedToSelection, { origin });
                onActionComplete?.();
            },
            disabled: selectedCount === 0,
        },
    ];
}
